// Autocomplete providers: turn `{ trigger, query }` from `useAutocomplete`
// into a list of suggestions for the menu.
//
// Each provider returns a `Suggestion[]`. The shape is generic so the
// menu doesn't need to know what kind of token it's rendering. The
// composer wires three concrete providers:
//
//   - `@` → user-directory search (local-first, then server)
//   - `#` → joined-rooms-in-active-org match, then server-side public-rooms
//   - `:` → emoji-mart `SearchIndex.search` (requires query.length >= 2)
//
// Server fan-out is debounced at the caller (the menu) so a fast
// typist doesn't fire a request per keystroke. Local lookups
// (joined rooms, in-cache user-directory results) run synchronously
// so the menu paints with stable rows on the first keystroke.

import { SearchIndex, init as initEmojiSearch } from "emoji-mart";
import emojiData from "@emoji-mart/data";

import type { RoomSearchResult, RoomSummary, UserDirectoryEntry } from "~/lib/matrix";

/**
 * One suggestion in the autocomplete menu. The menu renders the row
 * from these fields directly — providers are responsible for
 * formatting `primary` / `secondary` to match the brief's row spec.
 *
 * `replacement` is what the composer inserts on commit. The plain
 * text is the visible body; the optional `html` is the Matrix-custom
 * pill the composer pairs in `formatted_body`.
 */
export interface Suggestion {
  /** Stable React key. */
  id: string;
  /** Primary text. `type-body-md` 600. */
  primary: string;
  /** Secondary text (e.g. `@user:server`). `type-body-sm` muted. */
  secondary: string | null;
  /** Leading visual: avatar URL (user), `#` glyph (room), or emoji. */
  leading: SuggestionLeading;
  /** Plain-text replacement for the composer body. */
  replacement: string;
  /** Matrix-custom HTML for `formatted_body`. Optional. */
  replacementHtml?: string;
  /** Mentioned user's MXID, set only for `@` user suggestions. Carried
   *  through the composer so sends attach `m.mentions.user_ids`. */
  mxid?: string;
  /** A `@room` row: the composer records a sentinel mention token so
   *  `serialiseFormattedBody` sets `m.mentions.room` instead of a user id. */
  mentionRoom?: boolean;
  ownAgent?: { ownerMxid: string; roomId: string; scopeId: string };
}

export type SuggestionLeading =
  | { kind: "avatar"; url: string | null; name: string }
  | { kind: "glyph"; char: string }
  | { kind: "emoji"; native: string };

/* ── User directory provider ─────────────────────────────────────── */

/** Build a `<a>` mention pill for `formatted_body`. Spec link is
 *  `https://matrix.to/#/<userId>`. Both the user id and display name
 *  are user-supplied and land inside the HTML, so both are escaped:
 *  HTML-attribute escaping keeps a historical id containing `"` from
 *  breaking out of the `href` attribute while a browser still decodes
 *  the entity back to the raw sigil the matrix.to parser expects. */
function buildUserPill(userId: string, displayName: string): string {
  const escapedName = escapeHtml(displayName);
  const href = `https://matrix.to/#/${escapeHtml(userId)}`;
  return `<a href="${href}">${escapedName}</a>`;
}

export interface OwnAgentSnapshot {
  ready: boolean;
  ownerMxid: string;
  agentMxid: string;
  handle: string;
  roomId: string;
  scopeId: string;
}

export interface UserAutocompleteOptions {
  /** Query string the user typed after `@`. */
  query: string;
  /**
   * Local cache of users — typically the union of room members from
   * the current room and recent results from `searchUserDirectory`.
   * Searched first so the first keystroke paints without a network
   * round-trip.
   */
  local: readonly UserDirectoryEntry[];
  /**
   * Server-side fetch. Only called when local results are empty; the
   * menu debounces against this hook (200ms). Returns up to 10 rows.
   */
  searchUserDirectory: (term: string) => Promise<UserDirectoryEntry[]>;
  /**
   * Whether the current user has the power level to notify the whole
   * room. When true a `@room` row leads the list; when false it is
   * hidden so a non-privileged sender can't offer a mention the
   * server would reject. Sourced by the embedder from `canMentionRoom`.
   */
  allowRoomMention: boolean;
  ownAgent?: OwnAgentSnapshot | null;
}

/** Sentinel localparts a `@room`-style mention expands to. A user
 *  typing any prefix of these (or nothing) is offered the room row. */
const ROOM_MENTION_TRIGGERS = ["room", "channel", "here", "everyone", "all"] as const;

function isRoomMentionQuery(query: string): boolean {
  if (query.length === 0) return true;
  const needle = query.toLowerCase();
  return ROOM_MENTION_TRIGGERS.some((trigger) => trigger.startsWith(needle));
}

function roomMentionSuggestion(): Suggestion {
  return {
    id: "mention:room",
    primary: "@room",
    secondary: "Notify everyone in this room",
    leading: { kind: "glyph", char: "@" },
    replacement: "@room",
    mxid: undefined,
    mentionRoom: true,
  };
}

/**
 * Resolve `@` autocomplete suggestions. Returns `local` matches
 * synchronously (deduped) and a promise for the merged list once the
 * server-side fetch resolves. Callers (the menu) pick the snapshot
 * they want to render — local-first paints fast, server-merged
 * stays accurate.
 *
 * When `allowRoomMention` is true and the query is a prefix of a
 * room-mention trigger (or empty), a leading `@room` row is prepended
 * so a privileged sender can notify the whole channel.
 */
function ownAgentSuggestion(a: OwnAgentSnapshot): Suggestion {
  return {
    id: `user:${a.agentMxid}`,
    primary: a.handle,
    secondary: `Your agent · ${a.agentMxid}`,
    leading: { kind: "avatar", url: null, name: a.handle },
    replacement: a.handle,
    replacementHtml: buildUserPill(a.agentMxid, a.handle),
    mxid: a.agentMxid,
    ownAgent: { ownerMxid: a.ownerMxid, roomId: a.roomId, scopeId: a.scopeId },
  };
}

export function resolveUserSuggestions({
  query,
  local,
  searchUserDirectory,
  allowRoomMention,
  ownAgent,
}: UserAutocompleteOptions): {
  immediate: Suggestion[];
  fetch: () => Promise<Suggestion[]>;
  reservedUnavailable?: boolean;
} {
  const q = query.trim().toLowerCase();
  const handleNoAt = ownAgent?.handle.toLowerCase().slice(1) ?? "";
  const isReserved = ownAgent != null && (q === "agent" || (handleNoAt !== "" && q === handleNoAt));
  const isAgentPrefix = q.length > 0 && "agent".startsWith(q);
  const candidate = ownAgent?.ready ? ownAgentSuggestion(ownAgent) : null;

  if (isReserved) {
    if (!candidate) {
      return { immediate: [], fetch: async () => [], reservedUnavailable: true };
    }
    return { immediate: [candidate], fetch: async () => [candidate] };
  }

  const localMatches = filterUsers(local, query).map(toUserSuggestion);
  const room = allowRoomMention && isRoomMentionQuery(query) ? roomMentionSuggestion() : null;
  const leadWithOwn = (rows: Suggestion[]) =>
    candidate && isAgentPrefix ? [candidate, ...rows] : rows;
  const compose = (rows: Suggestion[]) => (room ? [room, ...rows] : rows);
  const base = localMatches.filter((s) => !(candidate && s.id === candidate.id)).slice(0, 10);
  return {
    immediate: compose(leadWithOwn(base)),
    fetch: async () => {
      const remote = await searchUserDirectory(query);
      const seen = new Set([...base.map((s) => s.id), ...(candidate ? [candidate.id] : [])]);
      const merged = [...base];
      for (const entry of remote) {
        const id = `user:${entry.userId}`;
        if (seen.has(id)) continue;
        seen.add(id);
        merged.push(toUserSuggestion(entry));
        if (merged.length >= 10) break;
      }
      return compose(leadWithOwn(merged));
    },
  };
}

function filterUsers(list: readonly UserDirectoryEntry[], query: string): UserDirectoryEntry[] {
  if (query.length === 0) return list.slice(0, 10);
  const needle = query.toLowerCase();
  return list.filter(
    (u) => u.displayName.toLowerCase().includes(needle) || u.userId.toLowerCase().includes(needle),
  );
}

function toUserSuggestion(entry: UserDirectoryEntry): Suggestion {
  return {
    id: `user:${entry.userId}`,
    primary: entry.displayName,
    secondary: entry.userId,
    leading: { kind: "avatar", url: entry.avatarUrl, name: entry.displayName },
    // Plain-text fallback per the ticket: `@<Display Name>`.
    replacement: `@${entry.displayName}`,
    replacementHtml: buildUserPill(entry.userId, entry.displayName),
    mxid: entry.userId,
  };
}

/* ── Room provider ───────────────────────────────────────────────── */

export interface RoomAutocompleteOptions {
  /** Query string the user typed after `#`. */
  query: string;
  /**
   * Joined rooms. The provider matches locally first so a user typing
   * `#des` sees their joined `design` room before the server returns
   * public-room candidates.
   */
  joinedRooms: readonly RoomSummary[];
  /**
   * Server-side public-rooms search. Pass the pre-bound function from
   * `MatrixContext`.
   */
  searchRooms: (term: string) => Promise<RoomSearchResult[]>;
}

export function resolveRoomSuggestions({
  query,
  joinedRooms,
  searchRooms,
}: RoomAutocompleteOptions): {
  immediate: Suggestion[];
  fetch: () => Promise<Suggestion[]>;
} {
  const local = filterRooms(joinedRooms, query).map(joinedRoomToSuggestion);
  return {
    immediate: local.slice(0, 10),
    fetch: async () => {
      const remote = await searchRooms(query);
      const seen = new Set(local.map((s) => s.id));
      const merged = [...local];
      for (const entry of remote) {
        const id = `room:${entry.roomId}`;
        if (seen.has(id)) continue;
        seen.add(id);
        merged.push(serverRoomToSuggestion(entry));
        if (merged.length >= 10) break;
      }
      return merged;
    },
  };
}

function filterRooms(list: readonly RoomSummary[], query: string): RoomSummary[] {
  // DMs are channel-only-style mentions; a `#peer-name` mention is
  // a category mismatch (and the server-side public-rooms response
  // already excludes them). Filter here so local matches stay
  // consistent with the homeserver's contract.
  const channels = list.filter((r) => !r.isDirect);
  if (query.length === 0) return channels.slice(0, 10);
  const needle = query.toLowerCase();
  return channels.filter(
    (r) =>
      r.name.toLowerCase().includes(needle) ||
      (r.topic ?? "").toLowerCase().includes(needle) ||
      r.id.toLowerCase().includes(needle),
  );
}

function joinedRoomToSuggestion(room: RoomSummary): Suggestion {
  return {
    id: `room:${room.id}`,
    primary: room.name,
    secondary: room.topic ?? room.id,
    leading: { kind: "glyph", char: "#" },
    replacement: `#${room.name}`,
    replacementHtml: buildRoomPill(room.id, room.name),
  };
}

function serverRoomToSuggestion(entry: RoomSearchResult): Suggestion {
  // Matrix's `PublicRoomsChunk.name` is spec-optional — the server
  // omits it for unnamed rooms, in which case the canonical alias
  // (or the raw room id, last resort) becomes the visible label.
  const label = entry.name || entry.canonicalAlias || entry.roomId;
  return {
    id: `room:${entry.roomId}`,
    primary: label,
    secondary: entry.canonicalAlias ?? entry.topic ?? entry.roomId,
    leading: { kind: "glyph", char: "#" },
    replacement: `#${label}`,
    replacementHtml: buildRoomPill(entry.canonicalAlias ?? entry.roomId, label),
  };
}

function buildRoomPill(roomIdOrAlias: string, name: string): string {
  // matrix.to room links: `https://matrix.to/#/<#alias:server>` or
  // `https://matrix.to/#/<!localpart:server>`. HTML-attribute escaping
  // keeps the fragment safe while a browser decodes the entity back to
  // the raw sigil the matrix.to parser expects. Note: raw room ids
  // without a `?via=` server hint can be unresolvable for users not on
  // the resident server; a follow-up will add `via=` once we have the
  // local user's homeserver in scope.
  const escapedName = escapeHtml(name);
  const href = `https://matrix.to/#/${escapeHtml(roomIdOrAlias)}`;
  return `<a href="${href}">${escapedName}</a>`;
}

/* ── Emoji provider ──────────────────────────────────────────────── */

/**
 * Narrow shape of an emoji-mart search hit. emoji-mart ships `any` on
 * its `SearchIndex.search` return type (the library has no public
 * type definitions for the hit), so the wrapper validates each entry
 * at the boundary rather than assert-casting. This is the
 * "boundary-narrowing" pattern from the TS conventions: receive
 * `unknown` from the library, prove the shape, then carry strict
 * types downstream.
 */
interface EmojiSearchHit {
  id: string;
  name: string;
  skins?: Array<{ native?: string }>;
}

function isEmojiSearchHit(value: unknown): value is EmojiSearchHit {
  if (!value || typeof value !== "object") return false;
  const v = value as { id?: unknown; name?: unknown; skins?: unknown };
  if (typeof v.id !== "string" || typeof v.name !== "string") return false;
  if (v.skins !== undefined && !Array.isArray(v.skins)) return false;
  return true;
}

let emojiInitPromise: Promise<void> | null = null;
function ensureEmojiSearchReady(): Promise<void> {
  if (emojiInitPromise) return emojiInitPromise;
  // `init` is synchronous in practice for `@emoji-mart/data`, but the
  // signature is async — the library lazily fetches a remote bundle
  // when called without a `data` arg. We pass the bundled data so the
  // search is offline-clean.
  emojiInitPromise = initEmojiSearch({ data: emojiData }, { caller: "nurion-chat-autocomplete" });
  return emojiInitPromise;
}

export interface EmojiAutocompleteOptions {
  query: string;
  /** Maximum suggestions to surface. Defaults to 10. */
  maxResults?: number;
}

/**
 * Resolve `:` autocomplete suggestions via emoji-mart's `SearchIndex`.
 * Returns a single async snapshot — emoji-mart's index is in-memory
 * after `init`, so the call is effectively synchronous after the
 * first run, but we honour the `Promise` return type so callers can
 * treat all three providers uniformly.
 */
export function resolveEmojiSuggestions({ query, maxResults = 10 }: EmojiAutocompleteOptions): {
  immediate: Suggestion[];
  fetch: () => Promise<Suggestion[]>;
} {
  // No immediate results — emoji-mart's index is the only source of
  // truth and an empty `immediate` keeps the contract uniform with
  // the user / room providers (where `immediate` is the local cache
  // hit). The menu paints a "Searching…" row until `fetch` resolves.
  return {
    immediate: [],
    fetch: async () => {
      await ensureEmojiSearchReady();
      const raw: unknown = await SearchIndex.search(query, {
        maxResults,
        caller: "nurion-chat",
      });
      if (!Array.isArray(raw) || raw.length === 0) return [];
      const out: Suggestion[] = [];
      for (const candidate of raw) {
        if (!isEmojiSearchHit(candidate)) continue;
        const native = candidate.skins?.[0]?.native;
        if (typeof native !== "string" || native.length === 0) continue;
        out.push({
          id: `emoji:${candidate.id}`,
          primary: candidate.name,
          secondary: `:${candidate.id}:`,
          leading: { kind: "emoji", native },
          replacement: native,
        });
        if (out.length >= maxResults) break;
      }
      return out;
    },
  };
}

/* ── Helpers ─────────────────────────────────────────────────────── */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
